import React, { useEffect, useMemo, useState } from 'react';
import axios from "../../utils/axiosConfig";
import { useParams } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import DealNavigation from "../../components/DealNavigation";
import BuyerDetailModal from "../../components/BuyerDetailModal";
import ShipperDetailModal from "../../components/ShipperDetailModal";
import FollowupModal from "../../components/FollowupModal";
import FixationModal from "../../components/FixationModal";
import { buildFixationPricingDefaults } from "../../utils/fixationDefaults";
import { calculateDealPayment } from "../../utils/dealPayment";
import { formatReportDate } from "../../utils/reportDate";

const INCOTERMS = ["EXW", "FCA", "FAS", "FOB", "CFR", "CIF", "CPT", "CIP", "DAP", "DPU", "DDP"];
const DEFAULT_PORT_OPTIONS = ["Jebel Ali", "Mundra", "Nhava Sheva", "Chennai", "Kandla"];
const DEFAULT_OTHER_INSTRUCTION = "1. Share clear and detailed loading photos with 25%, 50%, 75% and full container.\n2. Separate BL for each container.";
const ITEM_PRICE_MODES = ["Fixed", "UnFixed"];
const ITEM_LME_OPTIONS = [
  { value: "LME_CASH", label: "LME Cash" },
  { value: "LME_3MONTH", label: "LME 3Month" },
  { value: "LME_LOWEST", label: "LME Lowest" },
  { value: "LME_HIGHEST", label: "LME Highest" },
  { value: "LME_AVERAGE", label: "LME Average" }
];
const closedDealApprovalKey = (dealId) => `closedDealEditApproval:${dealId}`;
const closedDealApprovalConfig = (dealId) => {
  const approvalToken = dealId ? sessionStorage.getItem(closedDealApprovalKey(dealId)) : "";
  return approvalToken
    ? { headers: { "X-Closed-Deal-Edit-Approval": approvalToken } }
    : {};
};
const createEmptyItem = () => ({
  materialSupplier: "",
  materialBuyer: "",
  itemCode: "",
  attachment: "",
  itemName: "",
  itemId: "",
  packingId: "",
  quantity: "",
  priceMode: "Fixed",
  priceAdjustmentType: "",
  priceAdjustmentValue: "",
  lmeOption: "",
  rate: "",
  lme: ""
});

const toDecimalStringOrBlank = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(value);
  return Number.isNaN(num) ? "" : num.toFixed(2);
};

const getSubmittableItems = (itemRows) => (
  (Array.isArray(itemRows) ? itemRows : [])
    .filter((item) => item?.itemId && item?.quantity)
    .filter((item, index, list) => {
      if (!item.dealItemId) return true;
      return list.findIndex((row) =>
        row.dealItemId?.toString() === item.dealItemId.toString()
      ) === index;
    })
);

const NewDeal = () => {
  const navigate = useNavigate();
  const getTodayDateInput = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, "0");
    const day = String(today.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const initialForm = {
    dealId: '',
    dealIdNumber: '',
    dealDate: getTodayDateInput(),
    buyerId: '',
    shipperId: '',
    buyerBankId: '',
    accountNo: '',
    dealCurrency: 'USD',

    priceType: 'CIF',
    priceMode: 'Fixed',
    fixedAmount: '',
    unFixedAmount: '',
    lmeOption: "",
    fromDateType: "",
    fromDateType2: "",
    periodType: "",
    periodDays: "",
    isPercent: false,
    noOfContainer: '',
    qtyPerContainer: '',
    totalQty: '',
    shipmentDays: '',
    tolerance: '',
    instruction: DEFAULT_OTHER_INSTRUCTION,
    itemInstruction: '',

    commission: '',
    isPerPmt: false,
    commissionByBuyer: false,
    commissionBySupplier: true,
    buyerCommissionAmount: 0,
    supplierCommissionAmount: 0,
    totalCommission: 0,

    advancePayment: '',
    advanceIsPercent: true,

    paymentType: '',
    balanceType: '',
    balanceCad: false,

    balanceDays: {
      NET_WEIGHT: '',
      BL_DATE: '',
      COPY_DOC: '',
      DOC_DATE: '',
      ETA: ''
    }
  };
  const { id } = useParams();

  const [showDealInfo, setShowDealInfo] = useState(true);
  const [showPrice, setShowPrice] = useState(true);
  const [showContainer, setShowContainer] = useState(true);
  const [showCommission, setShowCommission] = useState(true);
  const [showPayment, setShowPayment] = useState(true);
  const [showOtherInstruction, setShowOtherInstruction] = useState(true);
  const [showTerms, setShowTerms] = useState(true);
  const [showBuyer, setShowBuyer] = useState(false);
  const [showShipper, setShowShipper] = useState(false);
  const [showFollowup, setShowFollowup] = useState(false);
  const [showFixation, setShowFixation] = useState(false);
  const [packingMaster, setPackingMaster] = useState([]);
  const [portMaster, setPortMaster] = useState([]);
  const [form, setForm] = useState(initialForm);
  const [editingId, setEditingId] = useState(null);
  const [buyers, setBuyers] = useState([]);
  const [shippers, setShippers] = useState([]);
  const [buyerBanks, setBuyerBanks] = useState([]);
  const [items, setItems] = useState([]);
  const [showItemGrid, setShowItemGrid] = useState(false);
  const [containers, setContainers] = useState([]);
  const [allContainerItemsSame, setAllContainerItemsSame] = useState(false);
  const [allContainerSame, setAllContainerSame] = useState(false);
  const [itemMaster, setItemMaster] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [terms, setTerms] = useState([]);
  const [termText, setTermText] = useState("");
  const [termSaving, setTermSaving] = useState(false);
  const fixationPricingDefaults = useMemo(() => buildFixationPricingDefaults(form, items), [form, items]);
  const formatDealNumber = (sequence, dealDate) => {
    const dateText = String(dealDate || "");
    const match = dateText.match(/^(\d{4})-(\d{2})-\d{2}/);
    const date = match ? null : new Date(dealDate || Date.now());
    if (!match && Number.isNaN(date.getTime())) return sequence?.toString() || "";
    const calendarYear = match ? Number(match[1]) : date.getFullYear();
    const month = match ? match[2] : String(date.getMonth() + 1).padStart(2, "0");
    const year = String(calendarYear).slice(-2);
    return `GM/${year}${month}${String(Number(sequence) || 0).padStart(3, "0")}`;
  };

  useEffect(() => {
    loadPacking();
    loadPorts();
  }, []);

  const loadPacking = async () => {
    try {
      const res = await axios.get("/packing");
      setPackingMaster(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error loading packing:", err);
      setPackingMaster([]);
    }
  };

  const loadPorts = async () => {
    try {
      const res = await axios.get("/ports");
      setPortMaster(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("Error loading ports:", err);
    }
  };
  const handleChange = async (e) => {
    const { name, value, type, checked } = e.target;

    const newValue = type === "checkbox" ? checked : value;
    if (name === "dealDate") {
      setForm((prev) => ({
        ...prev,
        dealDate: value,
        dealId: editingId && value
          ? prev.dealId.replace(/^GM\/\d{4}/, `GM/${value.slice(2, 4)}${value.slice(5, 7)}`)
          : prev.dealId
      }));
      if (!editingId) loadNextDealId(value);
      return;
    }

    if (name === "priceMode" && value === "Fixed") {
      setForm(prev => ({
        ...prev,
        priceMode: value,
        unFixedAmount: "",
        lmeOption: "",
        fromDateType: "",
        fromDateType2: "",
        periodType: "",
        periodDays: ""
      }));
    }
    setForm((prev) => ({
      ...prev,
      [name]: newValue
    }));

    // Buyer change → load banks
    if (name === "buyerId") {
      if (!value) {
        setBuyerBanks([]);
        setForm(prev => ({
          ...prev,
          buyerId: "",
          buyerBankId: "",
          accountNo: ""
        }));
        return;
      }

      try {
        const res = await axios.get(`/newdeal/buyer-banks/${value}`);

        const banks = res.data || [];
        const onlyBank = banks.length === 1 ? banks[0] : null;
        setBuyerBanks(banks);

        // 🔥 VERY IMPORTANT
        setForm(prev => ({
          ...prev,
          buyerId: value,
          buyerBankId: onlyBank ? onlyBank.BankID.toString() : "",
          accountNo: onlyBank?.AccNo || ""
        }));

      } catch (err) {
        console.error(err);
      }

      return; // prevent double setForm
    }
    // Price Mode switch logic
    if (name === "priceMode") {
      setForm((prev) => ({
        ...prev,
        priceMode: value,
        fixedAmount: value === "Fixed" ? prev.fixedAmount : "",
        unFixedAmount: value === "UnFixed" ? prev.unFixedAmount : ""
      }));
    }

    // Auto-fill account number
    if (name === "buyerBankId") {
      const selectedBank = buyerBanks.find(
        (b) => b.BankID.toString() === value.toString()
      );

      setForm((prev) => ({
        ...prev,
        buyerBankId: value,
        accountNo: selectedBank?.AccNo || ""
      }));
    }
  };
  const validateForm = () => {
    const newErrors = {};

    if (!form.dealDate) newErrors.dealDate = "Deal Date is required";
    if (!form.shipperId) newErrors.shipperId = "Supplier is required";
    if (!form.buyerId) newErrors.buyerId = "Buyer is required";
    if (!form.buyerBankId) newErrors.buyerBankId = "Buyer Bank is required";
    const containerLimit = Number(form.noOfContainer);
    if (!Number.isInteger(containerLimit) || containerLimit < 1) {
      newErrors.noOfContainer = "Enter a valid No. of Container";
    }
    if (!form.totalQty) newErrors.totalQty = "Total Quantity is required";
    if (!form.shipmentDays) newErrors.shipmentDays = "Shipment Period is required";
    const itemRowsForValidation = showItemGrid ? items : getSubmittableItems(items);
    if (
      !itemRowsForValidation ||
      itemRowsForValidation.length === 0 ||
      itemRowsForValidation.some(i => !i.itemId || !i.quantity)
    ) {
      newErrors.items = "Please complete all item row fields";
    }
    const requiredContainerRows = allContainerSame ? 1 : containerLimit;
    if (Number.isInteger(containerLimit) && containers.length !== requiredContainerRows) {
      newErrors.containers = allContainerSame
        ? "Add one container row for all containers"
        : `Add exactly ${containerLimit} container${containerLimit === 1 ? "" : "s"}`;
    } else if (
      !containers ||
      containers.length === 0 ||
      containers.some(
        (c) =>
          !c.size ||
          !c.portLoading ||
          !c.portDischarge ||
          !c.deliveryPort
      )
    ) {
      newErrors.containers = "Complete container details";
    }
    if (!form.paymentType) {
      newErrors.paymentType = "Payment Type is required";
    }
    if (form.paymentType === "CUSTOM" && !form.balanceType) {
      newErrors.balanceType = "Balance Type is required";
    }
    if (form.priceMode === "UnFixed") {
      if (!form.unFixedAmount)
        newErrors.unFixedAmount = "UnFixed Amount is required";
      else if (form.isPercent && Number(form.unFixedAmount) > 100)
        newErrors.unFixedAmount = "UnFixed percentage cannot exceed 100";

      if (!form.lmeOption)
        newErrors.lmeOption = "LME Option is required";

      if (!form.fromDateType)
        newErrors.fromDateType = "From Date Type is required";

      if (!form.periodType)
        newErrors.periodType = "Period Type is required";

      if (!form.periodDays)
        newErrors.periodDays = "Period Days is required";
    }
    if (Number(form.commission || 0) > 0 && !form.commissionByBuyer && !form.commissionBySupplier) {
      newErrors.commissionBy = "Select Buyer or Supplier for commission";
    }
    if (
      form.paymentType === "CUSTOM" &&
      form.advanceIsPercent &&
      Number(form.advancePayment) > 100
    ) {
      newErrors.advancePayment = "Advance payment percentage cannot exceed 100";
    }
    setErrors(newErrors);

    const messages = Object.values(newErrors);
    if (messages.length > 0) {
      setShowDealInfo(true);
      setShowPrice(true);
      setShowContainer(true);
      setShowCommission(true);
      setShowPayment(true);
      alert(`Please complete the required fields:\n- ${messages.join("\n- ")}`);
    }

    return messages.length === 0;
  };
  useEffect(() => {
    if (id) {
      setEditingId(id);
      axios.get(`/newdeal/${id}/edit-access`, closedDealApprovalConfig(id))
        .then(() => loadDealById())
        .catch((err) => {
          sessionStorage.removeItem(closedDealApprovalKey(id));
          alert(err.response?.data?.message || "Administrator approval is required to edit this closed deal");
          navigate("/transaction/new-deal/list", { replace: true });
        });
    } else {
      const freshForm = {
        ...initialForm,
        dealDate: getTodayDateInput()
      };
      setForm(freshForm);
      setEditingId(null);
      setBuyerBanks([]);
      setItems([]);
      setShowItemGrid(false);
      setContainers([]);
      setAllContainerItemsSame(false);
      setAllContainerSame(false);
      setErrors({});
      setIsSubmitted(false);
      setTerms([]);
      setTermText("");
      loadNextDealId(freshForm.dealDate);
    }
  }, [id]);
  const loadNextDealId = async (dealDateOverride) => {
    try {
      const res = await axios.get("/newdeal/next-id", {
        params: { dealDate: dealDateOverride || form.dealDate || undefined }
      });
      setForm((prev) => ({
        ...prev,
        dealIdNumber: res.data.nextDealId?.toString() || "",
        dealId: res.data.formattedDealId || formatDealNumber(res.data.nextDealSequence, prev.dealDate)
      }));
    } catch (err) {
      console.error("Error loading next Deal ID:", err);
      try {
        const res = await axios.get("/newdeal");
        const nextDealId = res.data.reduce(
          (maxId, deal) => Math.max(maxId, Number(deal.DealID) || 0),
          0
        ) + 1;
        const targetDate = dealDateOverride || form.dealDate;
        const nextSequence = res.data.reduce((maximum, deal) => {
          const match = String(deal.DealNo || "").match(/^GM\/\d{4}(\d{3,})$/);
          return Math.max(maximum, match ? Number(match[1]) : 0);
        }, 0) + 1;

        setForm((prev) => ({
          ...prev,
          dealIdNumber: nextDealId.toString(),
          dealId: formatDealNumber(nextSequence, targetDate)
        }));
      } catch (fallbackErr) {
        console.error("Error calculating next Deal ID:", fallbackErr);
      }
    }
  };
  const payload = {
    ...form,
    items,
    containers,
    payment: {
      paymentMode: form.paymentMode,
      advanceType: form.advanceType,
      advanceValue: form.advanceValue,
      advanceAmount: form.advanceAmount,
      balanceType: form.balanceType,
      balanceDays: form.balanceDays,
      balanceAmount: form.balanceAmount,
      isBalanceCAD: form.isBalanceCAD
    }
  };
  useEffect(() => {
    loadItemMaster();
  }, []);

  const loadItemMaster = async () => {
    try {
      const res = await axios.get(`${"/newdeal"}/items`);
      setItemMaster(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const getItemId = (item) => item?.ItemID ?? item?.ItemId ?? item?.itemId ?? "";
  const getItemCode = (item) => item?.ItemCode ?? item?.itemCode ?? "";
  const getItemName = (item) => item?.ItemName ?? item?.itemName ?? "";
  const normalizeItemValue = (value) => value?.toString().trim().toLowerCase();

  const findItemByCode = (itemCode) => {
    const normalizedCode = normalizeItemValue(itemCode);
    if (!normalizedCode) return undefined;

    return itemMaster.find(
      (itm) => normalizeItemValue(getItemCode(itm)) === normalizedCode
    );
  };

  const findItemById = (itemId) => {
    if (!itemId) return undefined;

    return itemMaster.find(
      (itm) => getItemId(itm).toString() === itemId.toString()
    );
  };

  const applySelectedItemToRow = (row, selectedItem) => ({
    ...row,
    itemId: getItemId(selectedItem).toString(),
    itemCode: getItemCode(selectedItem),
    itemName: getItemName(selectedItem)
  });


  const addItem = () => {
    setShowItemGrid(true);
    setItems(prev => {
      const hasDealInfoDraftOnly = prev.length === 1 &&
        !prev[0].dealItemId &&
        !prev[0].itemId &&
        !prev[0].packingId &&
        !prev[0].quantity &&
        !prev[0].rate &&
        !prev[0].priceAdjustmentType &&
        !prev[0].priceAdjustmentValue &&
        !prev[0].lmeOption;

      if (hasDealInfoDraftOnly) {
        return prev;
      }

      return [
        ...prev,
        createEmptyItem()
      ];
    });
  };
  const handleItemChange = (index, e) => {
    const { name, value } = e.target;
    if (name === "lme" && !/^\d*$/.test(value)) return;
    if (name === "rate" && !/^\d*$/.test(value)) return;
    if (name === "priceAdjustmentValue" && !/^\d*(\.\d{0,2})?$/.test(value)) return;
    if (name === "priceAdjustmentType" && (value.length > 5 || !/^-?\d*(\.\d{0,2})?$/.test(value))) return;
    const updatedItems = [...items];
    const currentRow = { ...createEmptyItem(), ...(updatedItems[index] || {}) };

    // 🔹 If Item Code is typed
    if (name === "itemCode") {
      const matchedItem = findItemByCode(value);

      if (matchedItem) {
        updatedItems[index] = applySelectedItemToRow(currentRow, matchedItem);
      } else if (!value.trim()) {
        updatedItems[index] = {
          ...currentRow,
          itemCode: "",
          itemId: "",
          itemName: ""
        };
      } else {
        updatedItems[index] = {
          ...currentRow,
          itemCode: value
        };
      }
    }

    else if (name === "packingId") {
      updatedItems[index] = {
        ...currentRow,
        packingId: value
      };
    }

    else if (name === "priceMode") {
      updatedItems[index] = {
        ...currentRow,
        priceMode: value,
        rate: value === "Fixed" ? currentRow.rate : "",
        priceAdjustmentType: value === "UnFixed" ? currentRow.priceAdjustmentType : "",
        priceAdjustmentValue: value === "UnFixed" ? currentRow.priceAdjustmentValue : "",
        lmeOption: value === "UnFixed" ? currentRow.lmeOption : ""
      };
    }

    // 🔹 If dropdown item selected
    else if (name === "itemId") {
      const selectedItem = findItemById(value);

      if (selectedItem) {
        updatedItems[index] = applySelectedItemToRow(currentRow, selectedItem);
      } else {
        updatedItems[index] = {
          ...currentRow,
          itemId: "",
          itemCode: "",
          itemName: ""
        };
      }
    }

    // 🔹 Other fields
    else {
      updatedItems[index] = {
        ...currentRow,
        [name]: value
      };
    }

    setItems(updatedItems);
  };

  const removeItem = (index) => {
    setItems(prev => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const addContainer = () => {
    const containerLimit = Number(form.noOfContainer);
    const rowLimit = allContainerSame ? 1 : containerLimit;

    if (!Number.isInteger(containerLimit) || containerLimit < 1) {
      setErrors(prev => ({
        ...prev,
        noOfContainer: "Enter No. of Container before adding containers"
      }));
      return;
    }

    if (containers.length >= rowLimit) {
      return;
    }

    setContainers(prev => [
      ...prev,
      {
        size: "",
        portLoading: "",
        portDischarge: "",
        deliveryPort: ""
      }
    ]);
  };

  const handleContainerChange = (index, e) => {
    const { name, value } = e.target;

    const updated = [...containers];
    updated[index][name] = value;

    setContainers(updated);
  };

  const removeContainer = (index) => {
    setContainers(prev => prev.filter((_, containerIndex) => containerIndex !== index));
  };
  useEffect(() => {
    if (allContainerSame && containers.length > 1) {
      setContainers(prev => prev.slice(0, 1));
    }
  }, [allContainerSame]);


  useEffect(() => {
    loadBuyers();
    loadShippers();
  }, []);
  useEffect(() => {
    const container = Number(form.noOfContainer);
    const qty = Number(form.qtyPerContainer);

    if (!allContainerSame && Number.isInteger(container) && container >= 0) {
      setContainers(prev => prev.length > container ? prev.slice(0, container) : prev);
    }

    if (container > 0 && qty > 0) {
      setForm(prev => ({
        ...prev,
        totalQty: container * qty
      }));
    }
  }, [form.noOfContainer, form.qtyPerContainer, allContainerSame]);

  const loadBuyers = async () => {
    try {
      const res = await axios.get(`${"/newdeal"}/buyers`);
      setBuyers(res.data);
    } catch (err) {
      console.error(err);
    }
  };
  const calculateCommission = () => {
    const totalQtyMt = parseFloat(form.totalQty || 0);
    const commissionValue = parseFloat(form.commission || 0);

    return totalQtyMt > 0 && commissionValue > 0
      ? totalQtyMt * commissionValue
      : 0;
  };


  useEffect(() => {
    const baseCommission = calculateCommission();
    const buyerCommissionAmount = form.commissionByBuyer ? baseCommission : 0;
    const supplierCommissionAmount = form.commissionBySupplier ? baseCommission : 0;
    setForm(prev => ({
      ...prev,
      buyerCommissionAmount,
      supplierCommissionAmount,
      totalCommission: buyerCommissionAmount + supplierCommissionAmount
    }));
  }, [form.commission, form.totalQty, form.commissionByBuyer, form.commissionBySupplier]);

  useEffect(() => {
    if (form.paymentType !== "CUSTOM") {
      setForm((prev) => ({
        ...prev,
        balanceType: "",
        balanceDays: {
          NET_WEIGHT: "",
          BL_DATE: "",
          COPY_DOC: "",
          DOC_DATE: "",
          ETA: ""
        }
      }));
    }
  }, [form.paymentType]);
  const loadShippers = async () => {
    try {
      const res = await axios.get(`${"/newdeal"}/shippers`);
      setShippers(res.data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchTerms = async (dealId) => {
    if (!dealId) return;
    try {
      const res = await axios.get(`/deal-terms/${dealId}`);
      setTerms(res.data || []);
    } catch (err) {
      console.error("Error loading terms:", err);
    }
  };

  const saveTerm = async () => {
    const text = termText.trim();
    if (!text) {
      alert("Enter terms and condition");
      return;
    }

    if (!editingId) {
      setTerms(prev => [
        ...prev,
        {
          TermID: `local-${Date.now()}`,
          TermDate: new Date().toISOString(),
          Terms: text,
          CreatedBy: "Shipping"
        }
      ]);
      setTermText("");
      return;
    }

    try {
      setTermSaving(true);
      await axios.post("/deal-terms", {
        dealId: editingId,
        terms: text
      });
      setTermText("");
      await fetchTerms(editingId);
    } catch (err) {
      console.error("Error saving terms and condition:", err.response?.data || err.message);
      alert(err.response?.data?.message || "Error saving terms and condition");
    } finally {
      setTermSaving(false);
    }
  };

  const loadDealById = async () => {
    try {
      const res = await axios.get(`/newdeal/${id}`);
      const data = res.data || {};
      const deal = data.deal || data;
      const loadedItems = Array.isArray(data.items) ? data.items : [];
      const uniqueLoadedItems = loadedItems.filter((item, index, list) => {
        const dealItemId = item.DealItemID ?? item.dealItemId;
        if (!dealItemId) return true;
        return list.findIndex((row) =>
          (row.DealItemID ?? row.dealItemId)?.toString() === dealItemId.toString()
        ) === index;
      });
      const loadedContainers = Array.isArray(data.containers) ? data.containers : [];
      const payment = Array.isArray(data.payment) ? data.payment[0] : data.payment;
      console.log("Loaded Deal:", data);

      if (!deal?.DealID) throw new Error("Deal data not found");

      // Set header form
      setForm(prev => ({
        ...prev,
        dealIdNumber: deal.DealID?.toString() || "",
        dealId: deal.DealNo || (deal.DealID ? formatDealNumber(deal.DealID, deal.DealDate) : ""),
        dealDate: deal.DealDate?.split("T")[0] || "",
        buyerId: deal.BuyerID?.toString() || "",
        shipperId: deal.ShipperID?.toString() || "",
        buyerBankId: deal.BuyerBankID?.toString() || "",
        accountNo: deal.AccountNo || "",
        dealCurrency: deal.DealCurrency || "",
        noOfContainer: deal.NoOfContainer || "",
        qtyPerContainer: deal.QtyPerContainer || "",
        totalQty: deal.TotalQty || "",
        shipmentDays: deal.ShipmentDays || "",
        tolerance: deal.Tolerance || "",
        instruction: deal.Instruction?.trim() ? deal.Instruction : DEFAULT_OTHER_INSTRUCTION,
        itemInstruction: deal.ItemInstruction || "",
        commission: deal.Commission || "",
        isPerPmt: false,
        commissionByBuyer: deal.CommissionByBuyer === true || deal.CommissionByBuyer === 1,
        commissionBySupplier: deal.CommissionBySupplier === true || deal.CommissionBySupplier === 1,
        buyerCommissionAmount: deal.BuyerCommissionAmount || 0,
        supplierCommissionAmount: deal.SupplierCommissionAmount || 0,
        totalCommission: deal.TotalCommissionAmount || 0,
        advancePayment: payment?.AdvanceValue ?? deal.AdvancePayment ?? "",
        advanceIsPercent: payment?.AdvanceType === "PERCENT",
        // 🔥 PRICE FIELDS
        priceMode: deal.PriceMode || "Fixed",
        fixedAmount: deal.FixedAmount || "",
        unFixedAmount: deal.UnFixedAmount || "",
        isPercent: deal.IsPercent === 1 ? true : false,
        lmeOption: deal.LMEOption || "",
        fromDateType: deal.FromDateType || "",
        fromDateType2: deal.FromDateType2 || "",
        periodType: deal.PeriodType || "",
        periodDays: deal.PeriodDays || "",
        // 🔥 PAYMENT AUTO LOAD
        paymentType: payment?.PaymentMode || "",
        balanceType: payment?.BalanceType || "",
        balanceCad: payment?.IsBalanceCAD === 1,

        balanceDays: {
          NET_WEIGHT:
            payment?.BalanceType === "NET_WEIGHT"
              ? payment?.BalanceDays
              : "",
          BL_DATE:
            payment?.BalanceType === "BL_DATE"
              ? payment?.BalanceDays
              : "",
          COPY_DOC:
            payment?.BalanceType === "COPY_DOC"
              ? payment?.BalanceDays
              : "",
          DOC_DATE:
            payment?.BalanceType === "DOC_DATE"
              ? payment?.BalanceDays
              : "",
          ETA:
            payment?.BalanceType === "ETA"
              ? payment?.BalanceDays
              : ""
        }
      }));


      setItems(
        uniqueLoadedItems.map(i => ({
          dealItemId: i.DealItemID || "",
          itemId: i.ItemId ?? i.ItemID ?? "",
          materialSupplier: i.MaterialSupplier || "",
          materialBuyer: i.MaterialBuyer || "",
          itemCode: i.ItemCode || "",
          attachment: i.Attachment == null ? "" : String(i.Attachment),
          itemName: i.ItemName || "",
          packingId: i.PackingID || "",
          quantity: i.Quantity,
          priceMode: i.PriceMode || (i.Rate ? "Fixed" : "UnFixed"),
          priceAdjustmentType: toDecimalStringOrBlank(i.PriceAdjustmentType),
          priceAdjustmentValue: toDecimalStringOrBlank(i.PriceAdjustmentValue),
          lmeOption: i.LMEOption || "",
          rate: i.Rate,
          lme: i.LME == null ? "" : String(Math.round(Number(i.LME)))
        }))
      );
      setShowItemGrid(uniqueLoadedItems.length > 0);
      setAllContainerItemsSame(
        deal.AllContainerItemsSame === true || Number(deal.AllContainerItemsSame) === 1
      );
      setAllContainerSame(
        deal.AllContainerSame === true || Number(deal.AllContainerSame) === 1
      );
      setContainers(
        loadedContainers.map(c => ({
          dealContainerId: c.DealContainerID || c.ID || "",
          size: c.Size,
          portLoading: c.PortLoading,
          portDischarge: c.PortDischarge,
          deliveryPort: c.DeliveryPort
        }))
      );
      if (deal.BuyerID) {
        try {
          const bankRes = await axios.get(`/newdeal/buyer-banks/${deal.BuyerID}`);
          setBuyerBanks(Array.isArray(bankRes.data) ? bankRes.data : []);
        } catch (bankErr) {
          console.error("Error loading buyer banks:", bankErr);
        }
      }
      fetchTerms(id);
    } catch (err) {
      console.error("Error loading deal:", err);
      alert(err.response?.data?.message || err.response?.data || err.message || "Error loading deal");
    }
  };
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitted(true);
    const isValid = validateForm();
    if (!isValid) return;

    const submitItems = getSubmittableItems(items);

    const { advanceAmount, balanceAmount } = calculateDealPayment(
      submitItems, form, allContainerItemsSame
    );

    const payload = {
      DealDate: form.dealDate,
      BuyerID: form.buyerId,
      ShipperID: form.shipperId,
      BuyerBankID: form.buyerBankId,
      AccountNo: form.accountNo,
      DealCurrency: form.dealCurrency,
      PriceType: form.priceType,
      PriceMode: form.priceMode,

      FixedAmount:
        form.priceMode === "Fixed" && form.fixedAmount !== ""
          ? Number(form.fixedAmount)
          : null,

      UnFixedAmount:
        form.priceMode === "UnFixed" && form.unFixedAmount !== ""
          ? Number(form.unFixedAmount)
          : null,

      IsPercent: form.priceMode === "UnFixed"
        ? (form.isPercent ? 1 : 0)
        : null,

      LMEOption: form.priceMode === "UnFixed"
        ? form.lmeOption
        : null,

      FromDateType: form.priceMode === "UnFixed"
        ? form.fromDateType
        : null,

      FromDateType2: form.priceMode === "UnFixed"
        ? form.fromDateType2
        : null,

      PeriodType: form.priceMode === "UnFixed"
        ? form.periodType
        : null,

      PeriodDays: form.priceMode === "UnFixed"
        ? form.periodDays
        : null,
      // FixedAmount: form.fixedAmount,
      // UnFixedAmount: form.unFixedAmount,
      NoOfContainer: form.noOfContainer,
      QtyPerContainer: form.qtyPerContainer,
      TotalQty: form.totalQty,
      ShipmentDays: form.shipmentDays,
      Tolerance: form.tolerance,
      Instruction: form.instruction,
      ItemInstruction: form.itemInstruction,
      AllContainerItemsSame: allContainerItemsSame ? 1 : 0,
      AllContainerSame: allContainerSame ? 1 : 0,
      Commission: form.commission,
      CommissionIsPercent: 0,
      CommissionByBuyer: form.commissionByBuyer ? 1 : 0,
      CommissionBySupplier: form.commissionBySupplier ? 1 : 0,
      BuyerCommissionAmount: form.buyerCommissionAmount || 0,
      SupplierCommissionAmount: form.supplierCommissionAmount || 0,
      TotalCommissionAmount: form.totalCommission || 0,
      AdvancePayment: form.advancePayment,
      EntryBy: 1,
      items: submitItems,
      containers: containers,
      terms: editingId ? [] : terms,
      payment: {
        paymentMode: form.paymentType,
        advanceType: form.advanceIsPercent ? "PERCENT" : "FIXED",
        advanceValue: form.advancePayment || 0,
        advanceAmount,
        balanceType: form.balanceType,
        balanceDays: form.balanceDays,
        balanceAmount,
        isBalanceCAD: form.balanceCad ? true : false
      }

    };

    try {
      let newDealId = null;
      if (editingId) {
        await axios.put(`${"/newdeal"}/${editingId}`, payload, closedDealApprovalConfig(editingId));
        sessionStorage.removeItem(closedDealApprovalKey(editingId));
      } else {
        const res = await axios.post("/newdeal", payload);
        newDealId = res.data?.dealId;
      }

      alert(editingId ? "Deal updated successfully" : "Deal saved successfully");
      const savedDealId = editingId || newDealId;
      if (savedDealId) {
        navigate(`/transaction/purchase-contract/${savedDealId}`);
      }

    } catch (err) {
      console.log("===== INSERT ERROR START =====");
      console.log(err);
      console.log(err.message);
      console.log(err.stack);
      console.log("===== INSERT ERROR END =====");
      alert(err.response?.data?.message || err.response?.data || err.message || "Save Failed");
    }
  };
  const sizeOptions = ["20 FT", "40 FT", "40 HC"];

  const portOptions = useMemo(() => {
    const names = portMaster
      .map((port) => port.PortName)
      .filter(Boolean);
    return names.length ? [...new Set(names)] : DEFAULT_PORT_OPTIONS;
  }, [portMaster]);
  const dealInfoItem = items[0] || createEmptyItem();

  return (
    <>
      {id && (
        <DealNavigation
          dealId={id}
          openBuyer={() => setShowBuyer(true)}
          openShipper={() => setShowShipper(true)}
          openFixation={() => setShowFixation(true)}
          openFollowup={() => setShowFollowup(true)}
        />
      )}
      <form onSubmit={handleSubmit} className="container-fluid mt-3">
      {/* ===== HEADER BADGE ===== */}
      <div className="mb-2">
        <span className="badge bg-success fs-6 px-3 py-2">
          New Deal
        </span>
      </div>

      <div className="container-fluid p-3">


        <div className="border rounded bg-white mb-3">

          <div
            className="px-3 py-2 border-bottom fw-bold text-muted d-flex justify-content-between align-items-center"
            style={{ cursor: "pointer" }}
            onClick={() => setShowDealInfo(!showDealInfo)}
          >
            <span>Deal Information</span>
            {editingId && (
              <div className="d-flex align-items-center gap-2 ms-auto me-2">
                <button
                  type="button"
                  className="btn btn-success btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowFixation(true);
                  }}
                >
                  Fixation
                </button>
                <button
                  type="button"
                  className="btn btn-outline-primary btn-sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowFollowup(true);
                  }}
                >
                  Followup
                </button>
              </div>
            )}
            <span>{showDealInfo ? "▲" : "▼"}</span>
          </div>

          {showDealInfo && (
            <div className="p-3">
              <div className="row">
                <div className="col-md-6 mb-3">
                  <div className="d-flex align-items-center">
                    <label htmlFor="dealId" className="me-3 fw-semibold" style={{ width: "140px" }}>
                      Deal ID
                    </label>
                    <input
                      id="dealId"
                      value={form.dealId}
                      className="form-control"
                      readOnly
                    />
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <div className="d-flex align-items-center">
                    <label htmlFor="dealDate" className="me-3 fw-semibold" style={{ width: "140px" }}>
                      Deal Date<span className="text-danger">*</span>
                    </label>
                    <input
                      type="date"
                      id="dealDate"
                      name="dealDate"
                      value={form.dealDate}
                      onChange={handleChange}
                      className={`form-control ${errors.dealDate ? "is-invalid" : ""}`}
                    />
                    {isSubmitted && errors.dealDate && (
                      <div className="text-danger small">{errors.dealDate}</div>
                    )}
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <div className="d-flex align-items-center">
                    <label htmlFor="supplierName" className="me-3 fw-semibold" style={{ width: "140px" }}>Supplier Name<span className="text-danger">*</span></label>
                    <select
                      name="shipperId"
                      value={form.shipperId || ""}
                      onChange={handleChange}
                      className={`form-control ${errors.shipperId ? "is-invalid" : ""}`}
                    >

                      <option value="">Select Supplier</option>
                      {shippers.map(ship => (
                        <option key={ship.ShipperID} value={ship.ShipperID}>
                          {ship.ShipperName}
                        </option>
                      ))}
                    </select>
                    {isSubmitted && errors.shipperId && (
                      <div className="text-danger small">{errors.shipperId}</div>
                    )}
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <div className="d-flex align-items-center">
                    <label htmlFor="buyerId" className="me-3 fw-semibold" style={{ width: "140px" }}>Buyer Name<span className="text-danger">*</span></label>
                    <select
                      name="buyerId"
                      value={form.buyerId || ""}
                      onChange={handleChange}
                      className={`form-control ${errors.buyerId ? "is-invalid" : ""}`}
                    >
                      <option value="">Select Buyer</option>
                      {buyers.map(buyer => (
                        <option key={buyer.BuyerID} value={buyer.BuyerID}>
                          {buyer.BuyerName}
                        </option>
                      ))}

                    </select>
                    {isSubmitted && errors.buyerId && (
                      <div className="text-danger small">{errors.buyerId}</div>
                    )}
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <div className="d-flex align-items-center">
                    <label htmlFor="dealCurrency" className="me-3 fw-semibold" style={{ width: "140px" }}>Deal Currency</label>
                    <select
                      id="dealCurrency"
                      name="dealCurrency"
                      value={form.dealCurrency}
                      onChange={handleChange}
                      className="form-control"
                    >
                      <option value="">Select</option>
                      <option>USD</option>
                      <option>INR</option>
                    </select>
                  </div>
                </div>

                <div className="col-md-6 mb-3">
                  <div className="d-flex align-items-center">
                    <label htmlFor="priceType" className="me-3 fw-semibold" style={{ width: "140px" }}>Type</label>
                    <select
                      id="priceType"
                      name="priceType"
                      value={form.priceType}
                      onChange={handleChange}
                      className="form-control"
                    >
                      {INCOTERMS.map((term) => (
                        <option key={term} value={term}>
                          {term}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="col-md-6 mb-3">
                  <div className="d-flex align-items-center">
                    <label htmlFor="buyerBank" className="me-3 fw-semibold" style={{ width: "140px" }}>Buyer Bank<span className="text-danger">*</span></label>
                    <select
                      name="buyerBankId"
                      value={form.buyerBankId || ""}
                      onChange={handleChange}
                      className={`form-control ${isSubmitted && errors.buyerBankId ? "is-invalid" : ""
                        }`}
                      disabled={!form.buyerId || buyerBanks.length === 1}
                    >
                      <option value="">Select Bank</option>
                      {buyerBanks.map(bank => (
                        <option key={bank.BankID} value={bank.BankID}>
                          {bank.BankName} - {bank.AccNo}
                        </option>
                      ))}
                    </select>
                    {isSubmitted && errors.buyerBankId && (
                      <div className="invalid-feedback d-block">
                        {errors.buyerBankId}
                      </div>
                    )}
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <div className="d-flex align-items-center">
                    <label htmlFor="accountNo" className="me-3 fw-semibold" style={{ width: "140px" }}>A/C No.</label>
                    <input
                      id="accountNo"
                      name="accountNo"
                      value={form.accountNo}
                      className="form-control"
                      readOnly
                    />
                  </div>
                </div>
                <div className="col-md-6 mb-3">
                  <div className="d-flex align-items-center">
                    <label className="me-3 fw-semibold" style={{ width: "140px" }}>LME</label>
                    <input
                      name="lme"
                      inputMode="numeric"
                      value={dealInfoItem.lme || ""}
                      onChange={(e) => handleItemChange(0, e)}
                      className="form-control"
                    />
                  </div>
                </div>
              </div>

            </div>

          )}
        </div>
        {/* Price Details section hidden temporarily */}
        {false && (
        <div className="border rounded bg-white mb-3">

          <div
            className="px-3 py-2 border-bottom fw-bold text-muted d-flex justify-content-between align-items-center"
            style={{ cursor: "pointer" }}
            onClick={() => setShowPrice(!showPrice)}
          >
            Price Details
            <span>{showPrice ? "▲" : "▼"}</span>
          </div>

          {showPrice && (
            <div className="p-3">

              {/* TYPE ROW */}
              <div className="d-flex align-items-center mb-3">
                <div style={{ width: "160px" }} className="fw-semibold">
                  Type
                </div>

                <div style={{ width: "250px" }}>
                  <select
                    name="priceType"
                    value={form.priceType}
                    onChange={handleChange}
                    className="form-control"
                  >
                    {INCOTERMS.map((term) => (
                      <option key={term} value={term}>
                        {term}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* FIXED ROW */}
              <div className="d-flex align-items-center mb-3">

                <div style={{ width: "160px" }} className="d-flex align-items-center">
                  <input
                    type="radio"
                    className="form-check-input me-2"
                    name="priceMode"
                    value="Fixed"
                    checked={form.priceMode === "Fixed"}
                    onChange={handleChange}
                  />
                  <span>Fixed Amount :</span>
                </div>

                <div style={{ width: "250px" }}>
                  <input
                    name="fixedAmount"
                    value={form.fixedAmount}
                    onChange={handleChange}
                    className="form-control"
                    disabled={form.priceMode !== "Fixed"}
                  />
                </div>
              </div>

              {/* UNFIXED ROW */}
              <div className="d-flex align-items-center">

                <div style={{ width: "160px" }} className="d-flex align-items-center">
                  <input
                    type="radio"
                    className="form-check-input me-2"
                    name="priceMode"
                    value="UnFixed"
                    checked={form.priceMode === "UnFixed"}
                    onChange={handleChange}
                  />
                  <span>UnFixed Amount :</span>
                </div>

                <div style={{ width: "250px" }}>
                  <input
                    name="unFixedAmount"
                    value={form.unFixedAmount}
                    onChange={handleChange}
                    className="form-control"
                    disabled={form.priceMode !== "UnFixed"}
                  />
                </div>
              </div>
              {form.priceMode === "UnFixed" && (
                <div className="mt-3">

                  {/* Unfixed Amount Row */}
                  <div className="d-flex align-items-center mb-3">
                    <span className="me-2">Unfixed @ USD</span>

                    <input
                      type="number"
                      name="unFixedAmount"
                      value={form.unFixedAmount}
                      onChange={handleChange}
                      className="form-control me-2"
                      style={{ width: "120px" }}
                    />
                    {isSubmitted && errors.unFixedAmount && (
                      <span className="text-danger small me-2">
                        {errors.unFixedAmount}
                      </span>
                    )}

                    <div className="form-check me-3">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        name="isPercent"
                        checked={form.isPercent}
                        onChange={handleChange}
                      />
                      <label className="form-check-label">%</label>
                    </div>

                    <span className="me-2">Of</span>

                    <select
                      name="lmeOption"
                      value={form.lmeOption || ""}
                      onChange={handleChange}
                      className="form-control"
                      style={{ width: "200px" }}
                    >
                      <option value="">Select Option</option>
                      <option value="LME_CASH">LME Cash</option>
                      <option value="LME_3MONTH">LME 3Month</option>
                      <option value="LME_LOWEST">LME Lowest</option>
                      <option value="LME_HIGHEST">LME Highest</option>
                    </select>
                  </div>

                  {/* Date Range Row */}
                  <div>
                    <div className="d-flex align-items-center">
                      <span className="me-2">From</span>

                      <select
                        name="fromDateType"
                        value={form.fromDateType || ""}
                        onChange={handleChange}
                        className="form-control me-2"
                        style={{ width: "180px" }}
                      >
                        <option value="">Select Date</option>
                        <option value="BL_DATE">BL Date</option>
                        <option value="ETA">ETA Date</option>
                        <option value="FIXED_DATE">Fixed Date</option>
                        <option value="LOADING_DATE">Loading Date</option>
                        <option value="ADVANCE_PAYMENT_DATE">Advance Payment Date</option>
                        <option value="CONTRACT_DATE">Contract Date</option>
                      </select>

                      <span className="me-2">To</span>

                      <select
                        name="periodType"
                        value={form.periodType || ""}
                        onChange={handleChange}
                        className="form-control me-2"
                        style={{ width: "150px" }}
                      >
                        <option value="">Select Period</option>
                        <option value="AFTER">After(+)</option>
                        <option value="BEFORE">Before(-)</option>
                      </select>

                      <input
                        type="number"
                        name="periodDays"
                        value={form.periodDays || ""}
                        onChange={handleChange}
                        className="form-control me-2"
                        style={{ width: "80px" }}
                      />

                      <select
                        name="fromDateType2"
                        value={form.fromDateType2 || ""}
                        onChange={handleChange}
                        className="form-control"
                        aria-label="Second from date type"
                        style={{ width: "180px" }}
                      >
                        <option value="">Select Date</option>
                        <option value="BL_DATE">BL Date</option>
                        <option value="ETA">ETA Date</option>
                        <option value="FIXED_DATE">Fixed Date</option>
                        <option value="LOADING_DATE">Loading Date</option>
                      </select>
                    </div>

                  </div>

                </div>
              )}
            </div>

          )}
        </div>
        )}



        <div className="border rounded bg-white mb-3">

          <div
            className="px-3 py-2 border-bottom fw-bold text-muted d-flex justify-content-between align-items-center"
            onClick={() => setShowContainer(!showContainer)}
            style={{ cursor: "pointer" }}
          >
            Container Information
            <span>{showContainer ? "▲" : "▼"}</span>
          </div>

          {showContainer && (
            <div className="p-3">

              {/* First Row */}
              <div className="row g-3 mb-3 align-items-start">
                <div className="col-md-auto" style={{ width: "170px" }}>
                  <label className="me-3 fw-semibold">No. of Container<span className="text-danger">*</span></label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    name="noOfContainer"
                    value={form.noOfContainer}
                    onChange={handleChange}
                    className={`form-control ${errors.noOfContainer ? "is-invalid" : ""}`}
                  />
                  {isSubmitted && errors.noOfContainer && (
                    <div className="text-danger small">{errors.noOfContainer}</div>
                  )}
                </div>

                <div className="col-md-auto" style={{ width: "220px" }}>
                  <label className="me-3 fw-semibold">Qty per Container (MT)</label>
                  <input
                    name="qtyPerContainer"
                    value={form.qtyPerContainer}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>

                <div className="col-md-auto" style={{ width: "220px" }}>
                  <label className="me-3 fw-semibold">Total Qty (MT)<span className="text-danger">*</span></label>
                  <input
                    name="totalQty"
                    value={form.totalQty}
                    readOnly
                    className={`form-control ${errors.totalQty ? "is-invalid" : ""}`}
                  />
                  {isSubmitted && errors.totalQty && (
                    <div className="text-danger small">{errors.totalQty}</div>
                  )}
                </div>

                <div className="col-md-auto" style={{ width: "210px" }}>
                  <label className="me-3 fw-semibold">Shipment Period (Days)<span className="text-danger">*</span></label>
                  <input
                    name="shipmentDays"
                    value={form.shipmentDays}
                    onChange={handleChange}
                    className={`form-control ${errors.shipmentDays ? "is-invalid" : ""}`}
                  />
                  {errors.shipmentDays && (
                    <div className="text-danger small">{errors.shipmentDays}</div>
                  )}
                </div>

                <div className="col-md-auto" style={{ width: "170px" }}>
                  <label className="me-3 fw-semibold">Tolerance (%)</label>
                  <input
                    name="tolerance" type="number"
                    step="0.01"
                    value={form.tolerance}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
              </div>

              {/* Metal, LME and Type are also available in Deal Information */}
              {/* All Container Same */}
              <div className="d-flex align-items-center mb-3">
                <input
                  type="checkbox"
                  className="form-check-input me-2"
                  checked={allContainerItemsSame}
                  onChange={(e) => setAllContainerItemsSame(e.target.checked)}
                />
                <label className="me-4">All Container Item Same</label>

                <button
                  type="button"
                  onClick={addItem}
                  className="btn btn-outline-primary btn-sm"
                >
                  Add Items
                </button>
              </div>

              {/* Row-level pricing items table */}
              {showItemGrid && items.length > 0 && (
                <div>
                  <div className="table-responsive">
                    <table className="table table-bordered align-middle" style={{ minWidth: "1310px", tableLayout: "fixed" }}>
                    <thead>
                      <tr>
                        <th style={{ width: "45px" }}>Sr.No</th>
                        <th style={{ width: "220px" }}>Material Detail</th>
                        <th style={{ width: "120px" }}>Attachment</th>
                        <th style={{ width: "140px" }}>ISRI Code</th>
                        <th style={{ width: "125px" }}>Packing</th>
                        <th style={{ width: "90px" }}>
                          Qty <span className="text-danger">*</span>
                        </th>
                        <th style={{ width: "95px" }}>Type</th>
                        <th style={{ width: "95px" }}>Per. Of LME</th>
                        <th style={{ width: "90px" }}>Disc&amp;Prem</th>
                        <th style={{ width: "115px" }}>LME</th>
                        <th style={{ width: "95px" }}>Price</th>
                        <th style={{ width: "85px" }}>Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {items.map((item, index) => {
                        const rowPriceMode = item.priceMode || "Fixed";
                        const isUnfixed = rowPriceMode === "UnFixed";

                        return (
                          <tr key={index}>
                            <td>{index + 1}</td>
                            <td>
                              <select
                                name="itemId"
                                value={item.itemId?.toString() || ""}
                                onChange={(e) => handleItemChange(index, e)}
                                className="form-control form-control-sm"
                              >
                                <option value="">Select Material</option>
                                {itemMaster.map((itm) => (
                                  <option key={getItemId(itm)} value={getItemId(itm)}>
                                    {getItemName(itm)}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <input
                                name="attachment"
                                type="text"
                                value={item.attachment || ""}
                                onChange={(e) => handleItemChange(index, e)}
                                className="form-control form-control-sm"
                              />
                            </td>
                            <td>
                              <input
                                name="itemCode"
                                value={item.itemCode || ""}
                                className="form-control form-control-sm"
                                readOnly
                              />
                            </td>
                            <td>
                              <select
                                name="packingId"
                                value={item.packingId || ""}
                                onChange={(e) => handleItemChange(index, e)}
                                className="form-control form-control-sm"
                              >
                                <option value="">Select Packing</option>
                                {packingMaster.map((p) => (
                                  <option key={p.PackingID} value={p.PackingID}>
                                    {p.PackingName}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <input
                                name="quantity"
                                value={item.quantity || ""}
                                onChange={(e) => handleItemChange(index, e)}
                                className="form-control form-control-sm"
                              />
                            </td>
                            <td>
                              <select
                                name="priceMode"
                                value={rowPriceMode}
                                onChange={(e) => handleItemChange(index, e)}
                                className="form-control form-control-sm"
                              >
                                {ITEM_PRICE_MODES.map((mode) => (
                                  <option key={mode} value={mode}>
                                    {mode}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <input
                                name="priceAdjustmentValue"
                                inputMode="decimal"
                                placeholder="%"
                                onBlur={(e) => {
                                  if (e.target.value !== "") handleItemChange(index, {
                                    target: { name: "priceAdjustmentValue", value: Number(e.target.value).toFixed(2) }
                                  });
                                }}
                                value={item.priceAdjustmentValue || ""}
                                onChange={(e) => handleItemChange(index, e)}
                                className="form-control form-control-sm"
                                disabled={!isUnfixed}
                              />
                            </td>
                            <td>
                              <input
                                name="priceAdjustmentType"
                                inputMode="decimal"
                                placeholder="+/-"
                                onBlur={(e) => {
                                  if (e.target.value !== "" && e.target.value !== "-") {
                                    const formattedValue = Number(e.target.value).toFixed(2);
                                    if (formattedValue.length <= 5) handleItemChange(index, {
                                      target: { name: "priceAdjustmentType", value: formattedValue }
                                    });
                                  }
                                }}
                                value={item.priceAdjustmentType || ""}
                                onChange={(e) => handleItemChange(index, e)}
                                className="form-control form-control-sm"
                                maxLength={5}
                                disabled={!isUnfixed}
                              />
                            </td>
                            <td>
                              <select
                                name="lmeOption"
                                value={item.lmeOption || ""}
                                onChange={(e) => handleItemChange(index, e)}
                                className="form-control form-control-sm"
                                disabled={!isUnfixed}
                              >
                                <option value="">Select LME</option>
                                {ITEM_LME_OPTIONS.map((option) => (
                                  <option key={option.value} value={option.value}>
                                    {option.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                            <td>
                              <input
                                name="rate"
                                inputMode="numeric"
                                value={item.rate === "" || item.rate == null ? "" : Number(item.rate).toFixed(0)}
                                onChange={(e) => handleItemChange(index, e)}
                                className="form-control form-control-sm"
                                disabled={isUnfixed}
                              />
                            </td>
                            <td className="text-center">
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => removeItem(index)}
                              >
                                Delete
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    </table>
                  </div>
                  <div className="mt-3">
                    <label htmlFor="itemInstruction" className="form-label fw-semibold">
                      Item Instruction
                    </label>
                    <input
                      id="itemInstruction"
                      type="text"
                      name="itemInstruction"
                      value={form.itemInstruction}
                      onChange={handleChange}
                      className="form-control w-100"
                      placeholder="Enter item instruction"
                    />
                  </div>
                </div>
              )}

              {/* Previous items table hidden temporarily */}
              {false && items.length > 0 && (
                <table className="table table-bordered">
                  <thead>
                    <tr>
                      <th style={{ width: "70px" }}>Sr.No</th>
                      <th style={{ width: "160px" }}>
                        Material(Supplier)
                      </th>
                      <th style={{ width: "160px" }}>
                        Material(Buyer)
                      </th>
                      <th style={{ width: "160px" }}>
                        Item Code <span className="text-danger">*</span>
                      </th>
                      <th>Item Name</th>
                      <th>Packing</th>
                      <th>
                        Qty(MT) <span className="text-danger">*</span>
                      </th>
                      <th>
                        Price(PMT)
                      </th>
                      <th>
                        LME
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        {/* 🔹 SMALL ITEM CODE BOX */}
                        <td>
                          <input
                            name="materialSupplier"
                            value={item.materialSupplier || ""}
                            onChange={(e) => handleItemChange(index, e)}
                            className="form-control form-control-sm"
                            placeholder="Material Supplier"
                          />
                        </td>
                        <td>
                          <input
                            name="materialBuyer"
                            value={item.materialBuyer || ""}
                            onChange={(e) => handleItemChange(index, e)}
                            className="form-control form-control-sm"
                            placeholder="Material Buyer"
                          />
                        </td>
                        <td>
                          <input
                            name="itemCode"
                            value={item.itemCode || ""}
                            onChange={(e) => handleItemChange(index, e)}
                            className="form-control form-control-sm"
                            placeholder="Item Code"
                          />
                        </td>
                        <td>
                          <select
                            name="itemId"
                            value={item.itemId?.toString() || ""}
                            onChange={(e) => handleItemChange(index, e)}
                            className="form-control"
                          >
                            <option value="">Select Item</option>
                            {itemMaster.map((itm) => (
                              <option key={getItemId(itm)} value={getItemId(itm)}>
                                {getItemName(itm)}
                              </option>
                            ))}
                          </select>

                        </td>
                        <td>
                          <select
                            name="packingId"
                            value={item.packingId || ""}
                            onChange={(e) => handleItemChange(index, e)}
                            className="form-control"
                          >
                            <option value="">Select Packing</option>
                            {packingMaster.map((p) => (
                              <option key={p.PackingID} value={p.PackingID}>
                                {p.PackingName}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            name="quantity"
                            value={item.quantity}
                            onChange={(e) => handleItemChange(index, e)}
                            className="form-control"
                          />
                        </td>
                        <td>
                          <input
                            name="rate"
                            value={item.rate}
                            onChange={(e) => handleItemChange(index, e)}
                            className="form-control"
                          />
                        </td>
                        <td>
                          <input
                            type="number"
                            step="0.0001"
                            name="lme"
                            value={item.lme}
                            onChange={(e) => handleItemChange(index, e)}
                            className="form-control"
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {isSubmitted && errors.items && (
                <div className="text-danger small mt-2">
                  {errors.items}
                </div>
              )}
              {/* Checkbox + Add Button */}
              <div className="d-flex align-items-center mb-3">
                <input
                  type="checkbox"
                  className="form-check-input me-2"
                  checked={allContainerSame}
                  onChange={(e) => setAllContainerSame(e.target.checked)}
                />

                <label className="me-3">
                  All container's Size and Port Info. same
                </label>

                <button
                  type="button"
                  className="btn btn-success btn-sm"
                  onClick={addContainer}
                  disabled={
                    !Number.isInteger(Number(form.noOfContainer)) ||
                    Number(form.noOfContainer) < 1 ||
                    containers.length >= (
                      allContainerSame ? 1 : Number(form.noOfContainer)
                    )
                  }
                >
                  Add Container
                </button>
                {Number(form.noOfContainer) > 0 && (
                  <span className="ms-2 text-muted small">
                    {allContainerSame
                      ? `${containers.length} / 1 shared container row added`
                      : `${containers.length} / ${form.noOfContainer} containers added`}
                  </span>
                )}
              </div>
              {/* Containers Table */}
              {containers.length > 0 && (
                <table className="table table-bordered mt-3">
                  <thead>
                    <tr>
                      <th style={{ width: "70px" }}>Sr.No</th>
                      <th>
                        Size <span className="text-danger">*</span>
                      </th>
                      <th>
                        POL(PORT OF LOADING) <span className="text-danger">*</span>
                      </th>
                      <th>
                        PODs(PORT OF DISCHARGE) <span className="text-danger">*</span>
                      </th>
                      <th>
                        POD(PORT OF DELIVERY) <span className="text-danger">*</span>
                      </th>
                      <th style={{ width: "100px" }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {containers.map((container, index) => (
                      <tr key={index}>
                        <td>{index + 1}</td>
                        {/* SIZE DROPDOWN */}
                        <td>
                          <select
                            name="size"
                            value={container.size}
                            onChange={(e) => handleContainerChange(index, e)}
                            className="form-control"
                          >
                            <option value="">Select Size</option>
                            {sizeOptions.map((size, i) => (
                              <option key={i} value={size}>
                                {size}
                              </option>
                            ))}
                          </select>
                        </td>

                        {/* PORT LOADING */}
                        <td>
                          <select
                            name="portLoading"
                            value={container.portLoading}
                            onChange={(e) => handleContainerChange(index, e)}
                            className="form-control"
                          >
                            <option value="">Select POL</option>
                            {portOptions.map((port, i) => (
                              <option key={i} value={port}>
                                {port}
                              </option>
                            ))}
                          </select>
                        </td>

                        {/* PORT DISCHARGE */}
                        <td>
                          <select
                            name="portDischarge"
                            value={container.portDischarge}
                            onChange={(e) => handleContainerChange(index, e)}
                            className="form-control"
                          >
                            <option value="">Select PODs</option>
                            {portOptions.map((port, i) => (
                              <option key={i} value={port}>
                                {port}
                              </option>
                            ))}
                          </select>
                        </td>

                        {/* DELIVERY PORT */}
                        <td>
                          <select
                            name="deliveryPort"
                            value={container.deliveryPort}
                            onChange={(e) => handleContainerChange(index, e)}
                            className="form-control"
                          >
                            <option value="">Select POD</option>
                            {portOptions.map((port, i) => (
                              <option key={i} value={port}>
                                {port}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className="text-center">
                          <button
                            type="button"
                            className="btn btn-outline-danger btn-sm"
                            onClick={() => removeContainer(index)}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {isSubmitted && errors.containers && (
                <div className="text-danger small mt-2">
                  {errors.containers}
                </div>
              )}
            </div>

          )}
        </div>

        <div className="border rounded bg-white mb-3">
          <div
            className="px-3 py-2 border-bottom fw-bold text-muted d-flex justify-content-between align-items-center"
            onClick={() => setShowCommission(!showCommission)}
            style={{ cursor: "pointer" }}
          >
            Commission
            <span>{showCommission ? "▲" : "▼"}</span>
          </div>

          {showCommission && (
            <div className="p-3">
              <div className="row align-items-center mb-3">
                <div className="col-md-2">
                  <label className="fw-semibold">Commission By :</label>
                </div>

                <div className="col-md-6">
                  <div className="d-flex align-items-center gap-4">
                    <div className="form-check d-flex align-items-center">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="commissionByBuyer"
                        name="commissionByBuyer"
                        checked={form.commissionByBuyer}
                        onChange={handleChange}
                      />
                      <label className="form-check-label ms-1" htmlFor="commissionByBuyer">
                        Buyer
                      </label>
                    </div>

                    <div className="form-check d-flex align-items-center">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="commissionBySupplier"
                        name="commissionBySupplier"
                        checked={form.commissionBySupplier}
                        onChange={handleChange}
                      />
                      <label className="form-check-label ms-1" htmlFor="commissionBySupplier">
                        Supplier
                      </label>
                    </div>
                    {isSubmitted && errors.commissionBy && (
                      <span className="text-danger small">
                        {errors.commissionBy}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="row align-items-center">
                <div className="col-md-2">
                  <label className="fw-semibold">Commission :</label>
                </div>

                <div className="col-md-8">
                  <div className="d-flex align-items-center gap-2">
                    <span className="fw-medium">USD</span>

                    <input
                      type="number"
                      name="commission"
                      value={form.commission}
                      onChange={handleChange}
                      className="form-control"
                      style={{ width: "120px" }}
                    />
                    {isSubmitted && errors.commission && (
                      <span className="text-danger small">
                        {errors.commission}
                      </span>
                    )}
                    <div className="ms-3 fw-semibold">
                      Total Commission: {(Number(form.totalCommission) || 0).toFixed(2)} USD
                    </div>
                    <div className="ms-3 small text-muted">
                      Buyer: {(Number(form.buyerCommissionAmount) || 0).toFixed(2)} USD
                    </div>
                    <div className="small text-muted">
                      Supplier: {(Number(form.supplierCommissionAmount) || 0).toFixed(2)} USD
                    </div>

                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        <div className="border rounded bg-white mb-3">
          <div
            className="px-3 py-2 border-bottom fw-bold text-muted d-flex justify-content-between align-items-center"
            onClick={() => setShowPayment(!showPayment)}
            style={{ cursor: "pointer" }}
          >
            Payment Instruction
            <span>{showPayment ? "▲" : "▼"}</span>
          </div>

          {showPayment && (
            <div className="p-3">
              <div className="row">

                {/* 100% Payment */}
                <div className="col-12 mb-2">
                  <div className="d-flex align-items-center">
                    <div className="form-check me-3">
                      <input
                        type="radio"
                        className="form-check-input"
                        name="paymentType"
                        value="FULL_DOC"
                        checked={form.paymentType === "FULL_DOC"}
                        onChange={handleChange}
                      />
                      <label className="form-check-label">
                        100% Payment
                      </label>
                    </div>

                    <input
                      type="text"
                      className="form-control"
                      style={{ maxWidth: "350px" }}
                      value="Cash Against Doc Through Bank"
                      readOnly
                    />
                  </div>
                </div>

                {/* 100% Advance */}
                <div className="col-12 mb-2">
                  <div className="form-check">
                    <input
                      type="radio"
                      className="form-check-input"
                      name="paymentType"
                      value="ADVANCE_FULL"
                      checked={form.paymentType === "ADVANCE_FULL"}
                      onChange={handleChange}
                    />
                    <label className="form-check-label">
                      100% Advance Payment Against Sales Contract
                    </label>
                  </div>
                </div>

                {/* Payment Types */}
                <div className="col-12 mb-3">
                  <div className="form-check">
                    <input
                      type="radio"
                      className="form-check-input"
                      name="paymentType"
                      value="CUSTOM"
                      checked={form.paymentType === "CUSTOM"}
                      onChange={handleChange}
                    />
                    <label className="form-check-label">
                      Payment Types
                    </label>

                  </div>

                </div>
                {isSubmitted && errors.paymentType && (
                  <div className="text-danger small mt-2">
                    {errors.paymentType}
                  </div>
                )}
                {/* Advance Payment Section */}

                <div className="col-12">
                  <div className="d-flex align-items-center">


                    {/* ================= ADVANCE + BALANCE SECTION ================= */}
                    {form.paymentType === "CUSTOM" && (
                      <div className="col-12 mt-4">

                        {/* Advance Payment Row */}
                        <div className="d-flex align-items-center mb-3">

                          <label className="fw-semibold me-2">
                            Advance Payment : USD
                          </label>

                          <input
                            type="text"
                            inputMode="decimal"
                            name="advancePayment"
                            value={form.advancePayment}
                            onChange={(e) => {
                              const value = e.target.value;
                              if (value === "" || /^\d*\.?\d*$/.test(value)) {
                                handleChange(e);
                              }
                            }}
                            className="form-control me-2"
                            style={{ width: "140px" }}
                          />
                          {isSubmitted && errors.advancePayment && (
                            <span className="text-danger small me-2">
                              {errors.advancePayment}
                            </span>
                          )}

                          <div className="form-check d-flex align-items-center me-3">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="advanceIsPercent"
                              name="advanceIsPercent"
                              checked={form.advanceIsPercent}
                              onChange={handleChange}
                            />
                            <label className="form-check-label ms-1" htmlFor="advanceIsPercent">
                              %
                            </label>
                          </div>
                        </div>

                        {/* Balance Cash Against Documents Row */}
                        <div className="d-flex align-items-center mb-3">
                          <label className="fw-semibold me-2">
                            ({form.dealCurrency || "USD"} {calculateDealPayment(items, form, allContainerItemsSame).advanceAmount.toFixed(2)}) Advance Payment
                          </label>

                          <div className="form-check me-2">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              id="balanceCad"
                              name="balanceCad"
                              checked={form.balanceCad}
                              onChange={handleChange}
                            />
                          </div>

                          <input
                            type="text"
                            className="form-control"
                            style={{ maxWidth: "450px" }}
                            value="BALANCE CASH AGAINST DOCUMENTS THROUGH BANK"
                            readOnly
                          />
                        </div>

                        {/* Balance Payment Options */}

                        {[
                          { label: "Net weight receive date", value: "NET_WEIGHT" },
                          { label: "BL date", value: "BL_DATE" },
                          { label: "Copy document receive date", value: "COPY_DOC" },
                          { label: "Document receive date", value: "DOC_DATE" },
                          { label: "ETA", value: "ETA" }
                        ].map((option, index) => (
                          <div key={index} className="d-flex align-items-center mb-2">

                            <div className="form-check me-3">
                              <input
                                type="radio"
                                className="form-check-input"
                                name="balanceType"
                                value={option.value}
                                checked={form.balanceType === option.value}
                                onChange={handleChange}
                              />
                            </div>

                            <label className="me-3" style={{ width: "250px" }}>
                              Balance Payment Against
                            </label>

                            <input
                              type="number"
                              disabled={form.balanceType !== option.value}
                              value={form.balanceDays[option.value] || ""}
                              onChange={(e) =>
                                setForm((prev) => ({
                                  ...prev,
                                  balanceDays: {
                                    ...prev.balanceDays,
                                    [option.value]: e.target.value
                                  }
                                }))
                              }
                              className="form-control me-3"
                              style={{ width: "90px" }}
                            />
                            {/* <label className="me-3" style={{ width: "250px" }}>
                             Net Weight Receive Date
                            </label> */}

                            <span>{option.label}</span>

                          </div>
                        ))}

                      </div>
                    )}
                  </div>
                </div>

              </div>
            </div>
          )}
        </div>
        <div className="border rounded bg-white mb-3">
          <div
            className="px-3 py-2 border-bottom fw-bold text-muted d-flex justify-content-between align-items-center"
            onClick={() => setShowOtherInstruction(!showOtherInstruction)}
            style={{ cursor: "pointer" }}
          >
            Other Instruction
            <span>{showOtherInstruction ? "▲" : "▼"}</span>
          </div>

          {showOtherInstruction && (
            <div className="p-3">
              <textarea
                name="instruction"
                value={form.instruction}
                onChange={handleChange}
                className="form-control"
                rows="3"
                placeholder="Enter other instruction"
                aria-label="Other Instruction"
              />
            </div>
          )}
        </div>
        <div className="border rounded bg-white mb-3">
          <div
            className="px-3 py-2 border-bottom fw-bold text-muted d-flex justify-content-between align-items-center"
            onClick={() => setShowTerms(!showTerms)}
            style={{ cursor: "pointer" }}
          >
            Terms and Condition
            <span>{showTerms ? "â–²" : "â–¼"}</span>
          </div>

          {showTerms && (
            <div className="p-3">
              <div className="card mb-3">
                <div className="card-header">Terms and Condition List</div>
                <div className="card-body" style={{ maxHeight: 220, overflowY: "auto" }}>
                  {terms.length === 0 ? (
                    <div className="text-center text-muted">
                      No terms and condition has been added so far.
                    </div>
                  ) : (
                    <table className="table table-sm table-bordered mb-0">
                      <thead>
                        <tr>
                          <th style={{ width: "140px" }}>Date</th>
                          <th>Terms and Condition</th>
                          <th style={{ width: "140px" }}>User</th>
                        </tr>
                      </thead>
                      <tbody>
                        {terms.map((term) => (
                          <tr key={term.TermID}>
                            <td>{term.TermDate ? term.TermDate.split("T")[0] : ""}</td>
                            <td>{term.Terms}</td>
                            <td>{term.CreatedBy || "Shipping"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              <div className="card">
                <div className="card-header">Add Terms and Condition</div>
                <div className="card-body">
                  <div className="row mb-2">
                    <div className="col-md-6">Date : {formatReportDate(new Date())}</div>
                    <div className="col-md-6 text-end">User : Shipping</div>
                  </div>

                  <textarea
                    className="form-control"
                    rows="3"
                    placeholder="Enter terms and condition..."
                    value={termText}
                    onChange={(e) => setTermText(e.target.value)}
                  />

                  <div className="mt-2">
                    <button
                      type="button"
                      className="btn btn-success btn-sm me-2"
                      onClick={saveTerm}
                      disabled={termSaving}
                    >
                      {termSaving ? "Saving..." : "Save"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => setTermText("")}
                    >
                      Clear
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        {/* FOOTER BUTTONS */}
        <div className="text-end mt-3">
          <button className="btn btn-success me-2">
            Save
          </button>
          <button type="button" className="btn btn-secondary" onClick={() => navigate("/transaction/new-deal/list")}>
            Cancel
          </button>
        </div>

      </div>
      </form>
      <BuyerDetailModal
        show={showBuyer}
        dealId={id}
        onClose={() => setShowBuyer(false)}
      />
      <ShipperDetailModal
        show={showShipper}
        dealId={id}
        onClose={() => setShowShipper(false)}
      />
      <FixationModal
        show={showFixation}
        dealId={id}
        totalQty={form.totalQty}
        dealCurrency={form.dealCurrency}
        dealPricingDefaults={fixationPricingDefaults}
        onClose={() => setShowFixation(false)}
      />
      <FollowupModal
        show={showFollowup}
        dealId={id}
        onClose={() => setShowFollowup(false)}
      />
    </>
  );
};

export default NewDeal;
